import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import isEmpty from 'lodash-es/isEmpty';
import type { DeepReadonly } from 'ts-essentials';

import type { InitialQuery, InitialQueryParams } from '@jane/search/types';

export type InitialQueryState = DeepReadonly<{
  initialQuery: InitialQuery;
  queryParamsPresent: boolean;
}>;

const getInitialState = (): InitialQueryState => ({
  initialQuery: { canUseSearchState: true },
  queryParamsPresent: false,
});

const PERMISSIBLE_PARAMS: readonly string[] = [
  'brand',
  'category',
  'root_types',
  'query',
  'store_type',
  'brand_subtype',
  'store_id',
] satisfies InitialQueryParams[];

const isPermissibleParam = (param: string): param is InitialQueryParams =>
  PERMISSIBLE_PARAMS.includes(param);

const initialQuerySlice = createSlice({
  name: 'initialQuery',
  initialState: getInitialState(),
  reducers: {
    setInitialQuery: (state, action: PayloadAction<string>) => {
      const query = action.payload;
      const urlParams = new URLSearchParams(query);
      const queryObject: {
        [key in InitialQueryParams]?: {
          hard: boolean;
          value: string[];
        };
      } = {};

      urlParams.forEach((value, key) => {
        if (isPermissibleParam(key)) {
          queryObject[key] = {
            value: decodeURIComponent(value).split(','),
            hard: Boolean(
              urlParams.has('hard') && urlParams.get('hard')?.includes(key)
            ),
          };
        }
      });
      const initialQuery = { ...queryObject, canUseSearchState: false };

      return {
        ...state,
        initialQuery,
        queryParamsPresent: !isEmpty(queryObject),
      };
    },
    resetInitialQuery: (state) => {
      const hardParams = [...Object.entries(state.initialQuery)]
        .filter(([, param]) => typeof param === 'object' && param.hard)
        .reduce(
          (acc, current) => ({
            ...acc,
            [current[0]]: current[1],
          }),
          {}
        );

      const initialQuery =
        Object.keys(hardParams).length > 0
          ? { ...hardParams, canUseSearchState: true }
          : { canUseSearchState: true };

      return { ...state, initialQuery };
    },
  },
  selectors: {
    selectInitialQuery: (state) => state,
  },
});

export const {
  reducer: initialQueryReducer,
  actions: { setInitialQuery, resetInitialQuery },
  selectors: { selectInitialQuery },
} = initialQuerySlice;

export type InitialQueryActions =
  ReturnType<typeof resetInitialQuery> | ReturnType<typeof setInitialQuery>;
